import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { CloudUpload, Clear } from "@mui/icons-material";
import { toast } from "react-toastify";
import { parseVectorFile, parseProjectVectorFile } from "../api/FlaskAPI";
import { getErrorMessage } from "../api/errors";
import { useTranslation } from "react-i18next";

function getShape({ componentSource, responseData }) {
  const is_area_valid = responseData.is_area_valid;
  if (componentSource === "project") {
    const geojson = responseData.geojson;
    return {
      polyId: `uploaded-${Date.now()}`,
      geojson,
      is_area_valid,
    };
  } else {
    const coordinates = responseData.coordinates;
    return {
      polyId: `uploaded-${Date.now()}`,
      coordinates,
      is_area_valid,
    };
  }
}

export default function ShapeUploader({ componentSource, onShapeLoaded }) {
  const { t } = useTranslation();
  const [isShapeDrawn, setIsShapeDrawn] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState(null);

  const MAX_UPLOAD_SIZE = 5 * 1024 * 1024; // 5 MB

  const onDrop = useCallback(
    async (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (!file) return;

      if (file.size > MAX_UPLOAD_SIZE) {
        toast.error(t("upload.tooBig"));
        return;
      }

      setUploading(true);
      setFileName(file.name);

      const formData = new FormData();
      formData.append("file", file);

      try {
        const response =
          componentSource === "project"
            ? await parseProjectVectorFile(formData)
            : await parseVectorFile(formData);
        if (response.status !== 200) {
          toast.error(response?.data?.message || t("upload.failed"));
          return;
        }
        const shape = getShape({
          componentSource,
          responseData: response.data,
        });

        onShapeLoaded(shape);
        setIsShapeDrawn(true);

        if (!shape.is_area_valid) {
          toast.warning(t("area.toasts.invalidUploaded"));
        } else {
          toast.success(t("upload.success"));
        }
      } catch (error) {
        toast.error(getErrorMessage(error, t("upload.failed")));
        setFileName(null);
      } finally {
        setUploading(false);
      }
    },
    [componentSource, MAX_UPLOAD_SIZE, onShapeLoaded, t],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/geo+json": [".geojson"],
      "application/zip": [".zip"],
      "application/vnd.google-earth.kml+xml": [".kml"],
      "application/geopackage+sqlite3": [".gpkg"],
      "image/vnd.dxf": [".dxf"],
      "application/x-filegdb": [".gdb"],
    },
    multiple: false,
  });

  const handleClear = () => {
    setIsShapeDrawn(false);
    onShapeLoaded(null);
    setFileName(null);
  };

  return (
    <Box sx={{ width: "100%" }}>
      {!isShapeDrawn ? (
        <Box
          {...getRootProps()}
          sx={{
            border: "2px dashed",
            borderColor: isDragActive ? "primary.main" : "grey.400",
            borderRadius: "10px",
            p: 2,
            textAlign: "center",
            cursor: "pointer",
            transition: "border-color 0.2s",
            bgcolor: isDragActive ? "action.hover" : "transparent",
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: "action.hover",
            },
          }}
        >
          <input {...getInputProps()} />
          <CloudUpload sx={{ color: "primary.main", fontSize: 32, mb: 1 }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {isDragActive ? t("upload.drop") : t("upload.prompt")}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {t("upload.supported")}
          </Typography>
          {uploading && (
            <CircularProgress
              size={20}
              sx={{ mt: 1, display: "block", mx: "auto" }}
            />
          )}
        </Box>
      ) : (
        <Box
          sx={{
            border: "1px solid",
            borderColor: "success.main",
            borderRadius: "10px",
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            bgcolor: "success.light",
          }}
        >
          <Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "success.dark" }}
            >
              {t("upload.loaded")}
            </Typography>
            {fileName && (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {fileName}
              </Typography>
            )}
          </Box>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<Clear />}
            onClick={handleClear}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}
          >
            {t("common.clear")}
          </Button>
        </Box>
      )}
    </Box>
  );
}
